import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import courseService from 'ApiService/Admin/courseService';
import batchStructureService from 'ApiService/Admin/batchService';
import branchService from 'ApiService/Admin/branchService';
import Card from 'components/card';
import Checkbox from 'components/checkbox';
import InputField from 'components/fields/InputField';
import ConfirmationModal from 'pages/Common/ConfirmationModal';

import { AuthContext } from 'contexts/AuthContext';
import { convertDatesForApi } from 'pages/helpers/convertDatesForApi';
import { useFormKeyboardSubmit } from 'pages/Hooks/useStepperKeyboardNavigation';
import { UseContextProvider } from 'views/admin/main/users/newUser/contexts/StepperContext';

const DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const AddBatch = () => {
  const { state } = useContext(AuthContext);
  const role = state?.user?.role;

  const currentBranch =
    role === 'superadmin'
      ? state?.selectedBranch || ''
      : state?.user?.branch?._id || '';

  /* ================= STATE ================= */
  const [courses, setCourses] = useState([]);
  const [batchStructures, setBatchStructures] = useState([]);
  const [branches, setBranches] = useState([]);
  const [nextBatchNumber, setNextBatchNumber] = useState(null);

  const [isGlobal, setIsGlobal] = useState(false);

  const [selectedBranches, setSelectedBranches] = useState(
    currentBranch ? [currentBranch] : []
  );

  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [disable, setDisable] = useState(true);
  const [errors, setErrors] = useState([]);

  const [batchData, setBatchData] = useState({
    courseId: '',
    batchStructureId: '',
    batchNumber: '',
    startDate: '',
    endDate: '',
    fees: '',
    mode: 'offline',
    daySchedules: [],
  });

  /* ================= FETCH COURSES ================= */
  useEffect(() => {
    (async () => {
      try {
        const res = await courseService.getAllCourses(currentBranch);
        setCourses(res?.data?.courses || []);
      } catch {
        toast.error('Failed to fetch courses');
      }
    })();
  }, [currentBranch]);

  /* ================= FETCH BRANCHES ================= */
  useEffect(() => {
    (async () => {
      try {
        const res = await branchService.getAllBranches();
        setBranches(res?.data?.allBranches || []);
        if (currentBranch) setSelectedBranches([currentBranch]);
      } catch {
        toast.error('Failed to fetch branches');
      }
    })();
  }, [currentBranch]);

  /* ================= FETCH STRUCTURES ================= */
  const fetchStructures = async (courseId) => {
    try {
      const res = await batchStructureService.getBatchStructuresByCourse(
        courseId
      );
      setBatchStructures(res?.data?.batchStructures || []);
    } catch {
      toast.error('Failed to fetch batch structures');
      setBatchStructures([]);
    }
  };

  /* ================= HANDLERS ================= */

  // 🔥 GLOBAL LOGIC FROM COURSE (KEY FIX)
  const handleCourseChange = async (e) => {
    const courseId = e.target.value;

    const selectedCourse = courses.find((c) => c._id === courseId);
    const global = Boolean(selectedCourse?.isGlobal);

    setIsGlobal(global);

    // reset branches safely
    if (currentBranch) {
      setSelectedBranches([currentBranch]);
    } else {
      setSelectedBranches([]);
    }

    setBatchData({
      courseId,
      batchStructureId: '',
      batchNumber: '',
      startDate: '',
      endDate: '',
      fees: '',
      mode: 'Hybrid',
      daySchedules: [],
    });

    if (courseId) {
      await fetchStructures(courseId);
    } else {
      setBatchStructures([]);
    }
  };

  const toggleDay = (day) => {
    setBatchData((prev) => ({
      ...prev,
      daySchedules: prev.daySchedules.some((d) => d.day === day)
        ? prev.daySchedules.filter((d) => d.day !== day)
        : [...prev.daySchedules, { day, startTime: '', endTime: '' }],
    }));
  };

  const updateTime = (index, field, value) => {
    const updated = [...batchData.daySchedules];
    updated[index][field] = value;
    setBatchData((prev) => ({ ...prev, daySchedules: updated }));
  };

  /* ================= VALIDATION ================= */
  useEffect(() => {
    const invalid =
      !batchData.courseId ||
      !batchData.batchStructureId ||
      !batchData.startDate ||
      !batchData.endDate ||
      !batchData.fees ||
      batchData.daySchedules.length === 0 ||
      batchData.daySchedules.some((d) => !d.startTime || !d.endTime) ||
      (isGlobal && selectedBranches.length === 0);

    setDisable(invalid);
    setErrors(invalid ? ['Please fill all required fields'] : []);
  }, [batchData, selectedBranches, isGlobal]);

  /* ================= FETCH LATEST BATCH ================= */
  useEffect(() => {
    const fetchLatestBatch = async () => {
      if (!batchData.batchStructureId || selectedBranches.length === 0) {
        setNextBatchNumber(null);
        return;
      }

      try {
        const res = await courseService.getLatestBatch(
          batchData.batchStructureId,
          selectedBranches
        );
        setNextBatchNumber(res?.data?.nextBatchNumber ?? 1);
      } catch {
        setNextBatchNumber(null);
      }
    };

    fetchLatestBatch();
  }, [batchData.batchStructureId, selectedBranches]);

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    if (disable || loading) return;
    setLoading(true);

    try {
      const payload = convertDatesForApi(
        {
          fees: batchData.fees,
          mode: batchData.mode,
          startDate: batchData.startDate,
          endDate: batchData.endDate,
          daySchedules: batchData.daySchedules,
          courseStructure: batchData.batchStructureId,
          branches: isGlobal ? selectedBranches : [currentBranch],
          selectedBranch: currentBranch,
        },
        ['startDate', 'endDate']
      );

      const res = await courseService.addBatch(payload);

      if (res.data.success) {
        toast.success('Batch created successfully');

        setBatchData({
          courseId: '',
          batchStructureId: '',
          startDate: '',
          endDate: '',
          fees: '',
          mode: 'Hybrid',
          daySchedules: [],
        });

        setIsGlobal(false);
        setSelectedBranches(currentBranch ? [currentBranch] : []);
        setConfirmOpen(false);
        setDisable(true);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to create batch');
    } finally {
      setLoading(false);
    }
  };

  useFormKeyboardSubmit(handleSubmit, disable, loading);

  /* ================= UI ================= */
  const visibleBranches = isGlobal
    ? branches
    : branches.filter((b) => b._id === currentBranch);

  return (
    <div className="mt-3 w-full">
      <Card>
        <UseContextProvider>
          <div className="flex flex-col gap-6 p-4">
            <h4 className="mb-2 text-2xl font-bold text-gray-800 dark:text-white">
              Create New Batch
            </h4>

            {/* COURSE */}
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-800">
              <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                Select Course
              </label>
              <select
                value={batchData.courseId}
                onChange={handleCourseChange}
                className="w-full rounded-lg border border-gray-300 bg-white p-3 text-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-navy-600 dark:bg-navy-700 dark:text-gray-200"
              >
                <option value="">Select Course</option>
                {courses.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.courseName}
                  </option>
                ))}
              </select>
            </div>

            {/* STRUCTURE */}
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-800">
              <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                Select Batch Structure
              </label>
              <select
                value={batchData.batchStructureId}
                onChange={(e) =>
                  setBatchData((p) => ({
                    ...p,
                    batchStructureId: e.target.value,
                  }))
                }
                className="w-full rounded-lg border border-gray-300 bg-white p-3 text-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-navy-600 dark:bg-navy-700 dark:text-gray-200"
              >
                <option value="">Select Structure</option>
                {batchStructures.map((bs) => (
                  <option key={bs._id} value={bs._id}>
                    {bs.name}
                  </option>
                ))}
              </select>
            </div>

            {/* BRANCHES */}
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-800">
              <label className="mb-3 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                Select Branch{isGlobal && ' (Multiple)'}
              </label>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                {visibleBranches.map((branch) => {
                  const checked = selectedBranches.includes(branch._id);
                  const isCurrent = branch._id === currentBranch;

                  return (
                    <label
                      key={branch._id}
                      className={`flex cursor-pointer items-center gap-2 rounded-lg border p-3 transition-all ${
                        checked
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 bg-white dark:border-navy-600 dark:bg-navy-700'
                      } ${
                        !isGlobal && isCurrent
                          ? 'opacity-60'
                          : 'hover:border-blue-300'
                      }`}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={!isGlobal && isCurrent}
                        onChange={() => {
                          if (!isGlobal) return;
                          setSelectedBranches((prev) =>
                            prev.includes(branch._id)
                              ? prev.filter((id) => id !== branch._id)
                              : [...prev, branch._id]
                          );
                        }}
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-200">
                        {branch.name}
                        {isCurrent && (
                          <span className="ml-1 text-xs text-green-600">
                            (Current)
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* MODE & BATCH NUMBER */}
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-800">
                <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Batch Mode
                </label>
                <select
                  value={batchData.mode}
                  onChange={(e) =>
                    setBatchData((p) => ({ ...p, mode: e.target.value }))
                  }
                  className="w-full rounded-lg border border-gray-300 bg-white p-3 text-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-navy-600 dark:bg-navy-700 dark:text-gray-200"
                >
                  <option value="Online">Online</option>
                  <option value="Offline">Offline</option>
                  <option value="Hybrid">Hybrid</option>
                </select>
              </div>

              <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-800">
                <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Batch Number
                </label>
                <InputField
                  value={batchData.batchNumber}
                  onChange={(e) =>
                    setBatchData((p) => ({ ...p, batchNumber: e.target.value }))
                  }
                  placeholder="Enter batch number"
                />
                {nextBatchNumber !== null && (
                  <div className="mt-3 flex items-center gap-2 rounded-lg bg-green-50 p-2 dark:bg-green-900/20">
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      Next Batch Number:
                    </span>
                    <span className="rounded-lg bg-green-600 px-3 py-1 font-bold text-white">
                      {nextBatchNumber}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* DATES & FEES */}
            <div className="grid gap-6 md:grid-cols-3">
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-800">
                <InputField
                  label="Start Date"
                  type="date"
                  value={batchData.startDate}
                  onChange={(e) =>
                    setBatchData((p) => ({ ...p, startDate: e.target.value }))
                  }
                />
              </div>

              <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-800">
                <InputField
                  label="End Date"
                  type="date"
                  value={batchData.endDate}
                  onChange={(e) =>
                    setBatchData((p) => ({ ...p, endDate: e.target.value }))
                  }
                />
              </div>

              <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-800">
                <InputField
                  label="Fees"
                  value={batchData.fees}
                  onChange={(e) =>
                    setBatchData((p) => ({ ...p, fees: e.target.value }))
                  }
                  placeholder="Enter fees amount"
                />
              </div>
            </div>

            {/* DAYS & TIMINGS */}
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-800">
              <label className="mb-4 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                Days & Timings
              </label>

              <div className="mb-4 flex flex-wrap gap-3">
                {DAYS.map((day) => {
                  const isSelected = batchData.daySchedules.some(
                    (d) => d.day === day
                  );
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDay(day)}
                      className={`rounded-lg px-4 py-2 text-sm font-medium transition-all ${
                        isSelected
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-white text-gray-600 hover:bg-blue-50 dark:bg-navy-700 dark:text-gray-300'
                      }`}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>

              {batchData.daySchedules.length > 0 && (
                <div className="rounded-lg bg-white p-4 dark:bg-navy-700">
                  <div className="mb-2 flex items-center gap-4 text-sm font-semibold text-gray-500">
                    <span className="w-28">Day</span>
                    <span className="flex-1">Start Time</span>
                    <span className="flex-1">End Time</span>
                  </div>
                  <div className="space-y-3">
                    {batchData.daySchedules.map((item, idx) => (
                      <div key={item.day} className="flex items-center gap-4">
                        <span className="w-28 font-medium text-gray-700 dark:text-gray-200">
                          {item.day}
                        </span>
                        <div className="flex-1">
                          <InputField
                            type="time"
                            value={item.startTime}
                            onChange={(e) =>
                              updateTime(idx, 'startTime', e.target.value)
                            }
                          />
                        </div>
                        <div className="flex-1">
                          <InputField
                            type="time"
                            value={item.endTime}
                            onChange={(e) =>
                              updateTime(idx, 'endTime', e.target.value)
                            }
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ERRORS */}
            {errors.length > 0 && (
              <div className="rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
                {errors.map((e, i) => (
                  <p key={i} className="text-sm text-red-600 dark:text-red-400">
                    {e}
                  </p>
                ))}
              </div>
            )}

            {/* SUBMIT */}
            <div className="flex justify-end pt-4">
              <button
                disabled={disable || loading}
                onClick={() => setConfirmOpen(true)}
                className="rounded-xl bg-brand-900 px-10 py-3 text-white transition-all hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="h-5 w-5 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Creating...
                  </span>
                ) : (
                  'Add Batch'
                )}
              </button>
            </div>

            <ConfirmationModal
              isOpen={confirmOpen}
              onClose={() => !loading && setConfirmOpen(false)}
              onConfirm={handleSubmit}
              title="Confirm Batch Creation"
              message="Are you sure you want to create this batch?"
            />
          </div>
        </UseContextProvider>
      </Card>
    </div>
  );
};

export default AddBatch;
